import React, { useEffect, useMemo, useState } from "react";

/** ---------- Small UI bits ---------- */
const Chip = ({ children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="px-3 py-1.5 rounded-full text-sm border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 shadow-sm transition disabled:opacity-50"
  >
    {children}
  </button>
);

const SectionCard = ({ title, subtitle, children, footer }) => (
  <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    {(title || subtitle) && (
      <div className="mb-2">
        {title && <div className="text-sm font-semibold">{title}</div>}
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
      </div>
    )}
    {children}
    {footer && <div className="mt-3 flex justify-end gap-2">{footer}</div>}
  </div>
);

const ExperienceList = ({ experiences, onRemove }) => {
  if (!experiences?.length) return null;
  return (
    <SectionCard title="Your experiences" subtitle="Saved so far">
      <div className="space-y-2">
        {experiences.map((xp, i) => (
          <div key={`${xp.source || "ext"}-${xp.projectId || i}`} className="p-3 rounded border border-gray-200 bg-neutral-50/50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{xp.title || "Untitled"}</div>
                {xp.role && <div className="text-xs text-gray-600">Role: {xp.role}</div>}
                {xp.summary && <div className="text-xs text-gray-600 mt-1">{xp.summary}</div>}
                {xp.link && (
                  <a href={xp.link} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline mt-1 inline-block">
                    {xp.link}
                  </a>
                )}
                <div className="text-[11px] text-gray-400 mt-1">Source: {xp.source === "internal" ? "Project on Frontera" : "External"}</div>
              </div>
              <button
                onClick={() => onRemove(i)}
                className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const ExternalExperienceForm = ({ onCancel, onConfirm }) => {
  const [rows, setRows] = useState([{ title: "", role: "", summary: "", link: "" }]);
  const addRow = () => setRows(r => [...r, { title: "", role: "", summary: "", link: "" }]);
  const update = (i, key, val) => setRows(r => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const remove = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const canConfirm = rows.some(r => r.title.trim() || r.summary.trim());

  const confirm = () => {
    const cleaned = rows
      .map(r => ({
        source: "external",
        title: r.title.trim(),
        role: r.role.trim(),
        summary: r.summary.trim(),
        link: r.link.trim()
      }))
      .filter(r => r.title || r.summary);
    onConfirm(cleaned);
  };

  return (
    <SectionCard title="Add external experiences" subtitle="Add 1–3 (optional links)">
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-3 bg-neutral-50/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                placeholder="Project / Product"
                value={row.title}
                onChange={(e) => update(i, "title", e.target.value)}
              />
              <input
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                placeholder="Your role (e.g., Designer, PM)"
                value={row.role}
                onChange={(e) => update(i, "role", e.target.value)}
              />
            </div>
            <textarea
              rows={3}
              className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              placeholder="One–two lines on what you built or shipped"
              value={row.summary}
              onChange={(e) => update(i, "summary", e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <input
                className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                placeholder="Link (GitHub, site, demo) — optional"
                value={row.link}
                onChange={(e) => update(i, "link", e.target.value)}
              />
              {rows.length > 1 && (
                <button type="button" onClick={() => remove(i)} className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50">
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button type="button" onClick={addRow} className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50">
          + Add another
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={confirm}
            className="px-3 py-2 rounded-md bg-black text-white text-sm disabled:opacity-50"
          >
            Confirm
          </button>
        </div>
      </div>
    </SectionCard>
  );
};

const ProjectSuggestionStep = ({ projects, onConfirm, onSkip, saving }) => {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const confirm = () => {
    const mapped = projects
      .filter(p => selected.includes(p.id))
      .map(p => ({
        source: "internal",
        projectId: p.id,
        title: p.title || "",
        role: p.openRoles?.[0]?.roleTitle || "",
        summary: p.oneLiner || "",
        link: p.link || ""
      }));
    onConfirm(mapped);
  };

  return (
    <SectionCard title="Select projects to add" subtitle="From your Frontera projects">
      <div className="space-y-2">
        {projects.map(p => {
          const active = selected.includes(p.id);
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => toggle(p.id)}
              className={`w-full text-left p-3 rounded border ${active ? "bg-blue-50 border-blue-400" : "bg-white border-gray-200"}`}
            >
              <div className="font-medium">{p.title}</div>
              {p.oneLiner && <div className="text-xs text-gray-600">{p.oneLiner}</div>}
              {p.openRoles?.[0]?.roleTitle && (
                <div className="text-xs text-gray-500 italic">Role: {p.openRoles[0].roleTitle}</div>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onSkip} disabled={saving} className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50 disabled:opacity-50">
          Skip
        </button>
        <button
          onClick={confirm}
          disabled={!selected.length || saving}
          className="px-3 py-2 rounded-md bg-black text-white text-sm disabled:opacity-50"
        >
          {saving ? "Saving…" : "Confirm"}
        </button>
      </div>
    </SectionCard>
  );
};

/** ---------- Main Experience Section ---------- */
const ExperienceSection = ({
  userEmail,
  authToken,
  initial = [],        // optional prefilled experiences
  onSave,              // async (array) => void   <-- parent persists to backend
  onStepComplete,      // () => void              <-- advance to next step
  fetchUserProjects,   // async () => array       <-- provided by parent
}) => {
  const [phase, setPhase] = useState("question"); // question | suggestions | addMorePrompt | externalForm
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [experiences, setExperiences] = useState(initial);
  const [dirty, setDirty] = useState(false);

  // de-dup by (projectId || title+link)
  const mergeExperiences = (current, incoming) => {
    const keyOf = (x) => x.projectId ? `pid:${x.projectId}` : `ext:${(x.title||"").toLowerCase()}|${(x.link||"").toLowerCase()}`;
    const map = new Map(current.map(x => [keyOf(x), x]));
    incoming.forEach(x => map.set(keyOf(x), x));
    return Array.from(map.values());
  };

  const handlePick = async (choice) => {
    if (choice === "Yes") {
      setLoading(true);
      try {
        const list = await fetchUserProjects?.();
        const unique = (list || []).filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
        setSuggested(unique);
        setPhase(unique.length ? "suggestions" : "externalForm");
      } catch {
        setPhase("externalForm");
      } finally {
        setLoading(false);
      }
    } else {
      setPhase("addMorePrompt");
    }
  };

  const persist = async (next, nextPhase = "addMorePrompt") => {
    setSaving(true);
    try {
      await onSave?.(next);
      setExperiences(next);
      setPhase(nextPhase);
    } finally {
      setSaving(false);
    }
  };

  const confirmInternal = (selectedInternal) => {
    const merged = mergeExperiences(experiences, selectedInternal);
    setExperiences(merged);   // stage locally
    setDirty(true);           // mark unsaved
    setPhase("externalForm"); // open manual add
  };

  const addExternal = async (externalRows) => {
    const merged = mergeExperiences(experiences, externalRows);
    await persist(merged);
    setDirty(false);
  };

  const removeExperience = async (index) => {
    const next = experiences.filter((_, i) => i !== index);
    await persist(next);
  };

  const canContinue = useMemo(() => experiences.length > 0, [experiences]);

  return (
    <div className="space-y-3">
      {phase === "question" && (
        <SectionCard title="Have you worked on any projects before?">
          <div className="flex flex-wrap gap-2">
            {["Yes", "No", "Still learning"].map(opt => (
              <Chip key={opt} onClick={() => handlePick(opt)} disabled={loading}>
                {opt}
              </Chip>
            ))}
          </div>
          {loading && <div className="text-sm text-gray-500 mt-2">Loading your projects…</div>}
        </SectionCard>
      )}

      {phase === "suggestions" && suggested.length > 0 && (
        <ProjectSuggestionStep
          projects={suggested}
          onConfirm={confirmInternal}
          onSkip={() => setPhase("externalForm")}
          saving={saving}
        />
      )}

      {phase === "addMorePrompt" && (
        <SectionCard
          title="Want to add more?"
          subtitle="You can add external experiences too."
          footer={
            <>
              <button
                onClick={() => setPhase("externalForm")}
                className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50"
                disabled={saving}
              >
                + Add more
              </button>
              <button
                onClick={async () => {
                    if (dirty) { await persist(experiences); setDirty(false); }
                    onStepComplete();
                }}
                disabled={!canContinue || saving}
                className="px-3 py-2 rounded-md bg-black text-white text-sm disabled:opacity-50"
              >
                Continue
              </button>
            </>
          }
        />
      )}

      {phase === "externalForm" && (
        <ExternalExperienceForm onCancel={() => setPhase("addMorePrompt")} onConfirm={addExternal} />
      )}

      <ExperienceList experiences={experiences} onRemove={removeExperience} />
    </div>
  );
};

export default ExperienceSection;
